import { memo, useMemo, type ReactNode } from "react";
import { BranchHashCell } from "@/components/table/branchHash";
import { EmptyState } from "@/shared/ui/emptyState/public";
import type { CompareRun } from "./evalCompareModel";
import { comparePolicyRows } from "./evalCompareDiffModel";
import { CompareTable, compareRunColumns, diffTableRow } from "./CompareTable";
import tableStyles from "./CompareTable.module.css";

// memo 를 쓰는 이유: 정책 비교에서는 run 열이 수백 개일 수 있다. 상세 팝업 토글 같은 무관한
// 상위 리렌더마다 diff 행을 다시 계산하고 표 전체를 다시 비교하면, 팝업 뒤에서 수십 ms 를 쓴다.
// 뷰어가 전달하는 props(items memo·onOpenRun useCallback)는 identity 가 안정적이다. 그래서
// memo 가 실제로 효과가 있다.
export const PolicyCompareTable = memo(function PolicyCompareTable({
  items,
  onOpenRun,
  showRunSublabels = false,
}: {
  items: readonly CompareRun[];
  onOpenRun: (runId: string) => void;
  // 같은 정책의 run 열이 나란히 있는 정책 비교에서 켠다. seed 부제가 열을 구분한다.
  showRunSublabels?: boolean;
}): ReactNode {
  const rows = useMemo(() => comparePolicyRows(items), [items]);
  const columns = useMemo(
    () => compareRunColumns(items, onOpenRun, { showSublabel: showRunSublabels }),
    [items, onOpenRun, showRunSublabels],
  );
  if (!rows.some((row) => row.values.some((value) => value !== null))) {
    return <EmptyState size="sm">표시할 Policy 정보가 없습니다.</EmptyState>;
  }
  const hasDiffs = rows.some((row) => row.differs);
  return (
    <CompareTable
      groupHeader="Policy"
      leafHeader="항목"
      columns={columns}
      rows={rows.map((row) => {
        const rendered = diffTableRow(row, "Policy", row.key);
        if (row.key === "code") {
          rendered.cells = rendered.cells.map((cell, index) => ({
            ...cell,
            value:
              cell.value === null ? null : (
                <BranchHashCell
                  branch={items[index].run.summaryRow.branch}
                  hash={items[index].run.summaryRow.git_short_hash}
                />
              ),
          }));
        }
        return rendered;
      })}
      footer={
        hasDiffs ? (
          <p className={tableStyles.footnote}>실행 결과를 비교해 값이 다른 항목을 강조했습니다.</p>
        ) : null
      }
    />
  );
});
